'use client';

import { c as _c } from "react/compiler-runtime";
import { BarChart3, ChevronsUpDown, Download, Flame, Shuffle, Trophy, Users, Volume2, VolumeX } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { teamMvps } from '@/lib/fantasy-extras';
import { FANTASY_RECAP, FANTASY_RECAP_FILENAME } from '@/lib/fantasy-media';
import { awards, badBeats, builds, charts, oneManShows, powerRankings, seasonNuggets, selectedChartDefault, teams } from './_data';
import { DataRoom } from './DataRoom';
import { DownloadableChart } from './DownloadableChart';
import { fmtSigned } from './format';
import { InitialBadge } from './InitialBadge';
import { LeagueTiers } from './LeagueTiers';
import { ManagementMap } from './ManagementMap';
import { ManagerTag } from './ManagerTag';
import { MiniBar } from './MiniBar';
import { NuggetStat } from './NuggetStat';
import { PlayerPortrait } from './PlayerPortrait';
import { PlayerTile } from './PlayerTile';
import { RegretFiles } from './RegretFiles';
import { RunInBoard } from './RunInBoard';
import { TradeHeadToHead } from './TradeHeadToHead';
import { WhatIfShuffle } from './WhatIfShuffle';

// Module scope keeps the early returns out of the hook body, where any `return`
// between `setRecapDownloading(true)` and the reset would strand the button in
// its "Saving…" state. Never throws.
async function shareRecapVideo(url: string, filename: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Recap fetch failed: ${res.status}`);
    const blob = await res.blob();
    const file = new File([blob], filename, {
      type: 'video/mp4'
    });
    const navAny = navigator as Navigator & {
      canShare?: (data: ShareData & {
        files?: File[];
      }) => boolean;
    };
    if (navAny.canShare?.({
      files: [file]
    })) {
      await navigator.share({
        files: [file],
        title: 'IPL Fantasy Recap'
      });
      return;
    }
    // No Web Share for files: open the URL so iOS users can long-press
    window.open(url, '_blank', 'noopener');
  } catch (err) {
    // A user-cancelled share (AbortError) is not a failure — only fall back to
    // a new tab when something actually went wrong.
    if ((err as Error).name !== 'AbortError') {
      window.open(url, '_blank', 'noopener');
    }
  }
}
function useFantasyPageView() {
  const $ = _c(103);
  const [selectedTeam, setSelectedTeam] = useState("Son Doku");
  const [selectedChart, setSelectedChart] = useState(selectedChartDefault);
  const [mode, setMode] = useState("rankings");
  const activeTeam = teams.find(team => team.team === selectedTeam) ?? teams[0];
  const activeBuild = builds.find(team_0 => team_0.team === selectedTeam) ?? builds[0];
  let t0;
  if ($[0] !== selectedChart) {
    t0 = chart => chart.slug === selectedChart;
    $[0] = selectedChart;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const activeChart = charts.find(t0);
  const recapVideoRef = useRef(null);
  const [recapMuted, setRecapMuted] = useState(true);
  const [recapDownloading, setRecapDownloading] = useState(false);
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = recapVideoRef.current;
      if (!el) {
        return;
      }
      const io = new IntersectionObserver(entries => {
        for (const e of entries) {
          if (e.isIntersecting) {
            el.play().catch(_temp);
          } else {
            el.pause();
          }
        }
      }, {
        threshold: 0.4
      });
      io.observe(el);
      const sync = () => setRecapMuted(el.muted);
      el.addEventListener("volumechange", sync);
      return () => {
        io.disconnect();
        el.removeEventListener("volumechange", sync);
      };
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el_0 = recapVideoRef.current;
      if (!el_0) {
        return;
      }
      const next = !el_0.muted;
      el_0.muted = next;
      setRecapMuted(next);
      if (!next) {
        el_0.play().catch(_temp2);
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const toggleRecapMuted = t3;
  let t4;
  if ($[5] !== recapDownloading) {
    t4 = async () => {
      if (recapDownloading) {
        return;
      }
      const url = FANTASY_RECAP.web;
      const isMobile = /iPad|iPhone|iPod|Android/i.test(navigator.userAgent) || (navigator as Navigator & {
        canShare?: (d: ShareData & {
          files?: File[];
        }) => boolean;
      }).canShare !== undefined;
      if (!isMobile) {
        const a = document.createElement("a");
        a.href = url;
        a.download = FANTASY_RECAP_FILENAME;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }
      setRecapDownloading(true);
      await shareRecapVideo(url, FANTASY_RECAP_FILENAME);
      setRecapDownloading(false);
    };
    $[5] = recapDownloading;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const downloadRecap = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function jumpToSection(id) {
      setMode(() => id);
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const jumpToSection = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function openDossier(team_1) {
      setSelectedTeam(() => team_1);
      setMode("dossiers");
      document.getElementById("dossiers")?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const openDossier = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <style>{"\n        @keyframes fantasyFadeUp {\n          from {\n            opacity: 0;\n            transform: translateY(18px);\n          }\n          to {\n            opacity: 1;\n            transform: translateY(0);\n          }\n        }\n\n        /* Intentionally not gated behind prefers-reduced-motion \u2014 animations\n           are shown to everyone (owner's explicit choice for this site). */\n        .fantasy-reveal {\n          animation: fantasyFadeUp both;\n          animation-timeline: view();\n          animation-range: entry 6% cover 22%;\n        }\n      "}</style>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute inset-0 [background-image:linear-gradient(#f7f0e6_1px,transparent_1px),linear-gradient(90deg,#f7f0e6_1px,transparent_1px)] [background-size:38px_38px] opacity-[0.16]" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="pt-2 pb-6 text-white"><p className="mb-3 text-xs font-black tracking-[0.2em] text-[#fff4df]/80 uppercase">Irvine Premier League · 25/26</p><h1 className="font-sans text-5xl leading-[0.95] font-semibold sm:text-6xl lg:text-7xl">Sleepless in Seattle stays awake.</h1></div>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="my-6 w-full overflow-hidden border border-white/40 bg-neutral-950 shadow-[16px_16px_0_#24122f]"><div className="aspect-video w-full"><video ref={recapVideoRef} controls={true} muted={true} loop={true} playsInline={true} preload="metadata" poster={FANTASY_RECAP.poster} className="size-full" aria-label="Fantasy league season recap video"><source src={FANTASY_RECAP.web} type="video/mp4" /></video></div></div>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const t11 = !recapMuted;
  const t12 = recapMuted ? "Unmute recap" : "Mute recap";
  let t13;
  if ($[13] !== recapMuted) {
    t13 = recapMuted ? <VolumeX className="size-3.5" /> : <Volume2 className="size-3.5" />;
    $[13] = recapMuted;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const t14 = recapMuted ? "Tap for sound" : "Sound on";
  let t15;
  if ($[15] !== t11 || $[16] !== t12 || $[17] !== t13 || $[18] !== t14) {
    t15 = <button type="button" onClick={toggleRecapMuted} className="inline-flex items-center gap-2 border border-white/30 bg-white/5 px-3 py-1.5 text-[11px] font-black tracking-[0.2em] text-white/85 uppercase backdrop-blur transition hover:border-white/60 hover:bg-white/15 hover:text-white" aria-pressed={t11} aria-label={t12}>{t13}{t14}</button>;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Download className="size-3.5" />;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const t17 = recapDownloading ? "Preparing\u2026" : "Save the recap";
  let t18;
  if ($[21] !== downloadRecap || $[22] !== recapDownloading || $[23] !== t17) {
    t18 = <button type="button" onClick={downloadRecap} disabled={recapDownloading} className="inline-flex items-center gap-2 border border-white/30 bg-white/5 px-3 py-1.5 text-[11px] font-black tracking-[0.2em] text-white/85 uppercase backdrop-blur transition hover:border-white/60 hover:bg-white/15 hover:text-white disabled:opacity-60" aria-label="Save the recap video">{t16}{t17}</button>;
    $[21] = downloadRecap;
    $[22] = recapDownloading;
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t15 || $[26] !== t18) {
    t19 = <div className="mb-6 flex flex-wrap items-center justify-end gap-2">{t15}{t18}</div>;
    $[25] = t15;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = [["rankings", Trophy, "Power rankings"], ["dossiers", Users, "Team dossiers"], ["charts", BarChart3, "Chart room"]];
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== mode) {
    t21 = <div className="mt-auto flex flex-wrap gap-2 pt-4">{(t20 as const).map(t22 => {
        const [id_0, Icon, label] = t22;
        return <button key={id_0 as string} type="button" onClick={() => jumpToSection(id_0 as typeof mode)} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-black uppercase transition ${mode === id_0 ? "border-[#24122f] bg-[#f7f0e6] text-[#24122f]" : "border-white/60 bg-white/10 text-white hover:bg-white/20"}`}><Icon className="size-4" />{label as string}</button>;
      })}</div>;
    $[29] = mode;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== t19 || $[32] !== t21) {
    t22 = <section className="relative overflow-hidden border-b border-[#24122f] bg-[#4f6f3b]">{t8}<div className="relative mx-auto flex min-h-[92vh] max-w-7xl flex-col px-5 py-8 sm:px-8 lg:py-10">{t9}{t10}{t19}{t21}</div></section>;
    $[31] = t19;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div><p className="text-sm font-black text-[#8a3f31] uppercase">State of play</p><h2 className="mt-2 font-sans text-5xl leading-tight font-semibold">The standings are doing PR. The points are leaking.</h2></div>;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <p>First place is real. We are saying this slowly for the people still loading the group chat. <ManagerTag name="Rohit" /> is second in points, second by all-play, first in the table, three witnesses, same testimony. Sleepless in Seattle has earned this. The Staff has filed it in writing and the regime of Yang has acknowledged receipt.</p>;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <ManagerTag name="Teddy" />;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ManagerTag name="Jonathan" />;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  let t28;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <section className="fantasy-reveal mx-auto max-w-7xl px-5 py-16 sm:px-8"><div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">{t23}<div className="space-y-6 text-lg leading-8 font-semibold text-[#4f4358]">{t24}<p>Below him is where the season turns into legal discovery.{" "}{t25} has the most points and a third-place ribbon nobody wants on the fridge. {t26}{" "}has scored like a top-four team and sits ninth.{" "}<ManagerTag name="Leyland" /> has accepted exactly zero waiver moves and is, somehow, fourth, the Staff finds this personally insulting and would like the record to reflect it.</p></div></div></section>;
    t28 = <LeagueTiers />;
    $[38] = t27;
    $[39] = t28;
  } else {
    t27 = $[38];
    t28 = $[39];
  }
  let t29;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="mb-9 grid gap-6 lg:grid-cols-[0.8fr_1.2fr]"><div><p className="text-sm font-black text-[#e2c078] uppercase">Awards night</p><h2 className="mt-2 font-sans text-5xl font-semibold">No rings. No banner. Just exhibits A through K.</h2></div><p className="text-lg leading-8 font-semibold text-[#e8ddca]">Some of these confirm what the group chat already knew. Some are worse than that. Documents have been reviewed. Coffee has been ordered. Arguments are loaded and ready for delivery.</p></div>;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  let t30;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <section className="fantasy-reveal border-y border-[#24122f] bg-[#24122f] text-white"><div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">{t29}<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{awards.map(_temp3)}</div></div></section>;
    $[41] = t30;
  } else {
    t30 = $[41];
  }
  let t31;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end"><div><h2 className="font-sans text-5xl font-semibold">Power rankings, now inadmissible in court.</h2></div><p className="max-w-lg text-sm leading-6 font-bold text-[#6d5d70]">Weighted on table position, scoring, all-play expectation, schedule luck, roster churn, and the gut answer to: who would you least want to draw this week. Group-chat takes admitted only with footnotes attached.</p></div>;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  let t33;
  let t34;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <section id="rankings" className="fantasy-reveal border-y border-[#24122f] bg-[#fffaf1]"><div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">{t31}<div className="grid gap-4">{powerRankings.map(team_2 => {
            const isRelegation = team_2.rank >= 11;
            const tier = team_2.rank === 1 ? "champion" : team_2.rank <= 4 ? "top" : isRelegation ? "relegation" : "mid";
            const tierBg = {
              champion: "bg-[#fff5d6] border-[#e2c078]",
              top: "bg-[#f3ede0] border-[#d7c9b7]",
              mid: "bg-[#f7f0e6] border-[#d7c9b7]",
              relegation: "bg-[#f5dada] border-[#c8a0a0]"
            }[tier];
            const contractTag = {
              champion: "\uD83C\uDFC6 Picks the charity. Picks first.",
              top: "Collects waffles. Rides. Meals. Wishes.",
              mid: undefined,
              relegation: team_2.rank === 13 ? "Open mic. No filming." : team_2.rank === 12 ? "Busking duty. $20." : "Books the trip. Not OC."
            }[tier];
            return <div key={team_2.team} role="button" tabIndex={0} onClick={() => openDossier(team_2.team)} onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openDossier(team_2.team);
              }
            }} className={`relative grid cursor-pointer gap-5 border ${tierBg} p-5 shadow-[6px_6px_0_#24122f] transition hover:-translate-y-1 hover:shadow-[9px_9px_0_#24122f] focus:ring-4 focus:ring-[#4ea97e]/40 focus:outline-none md:grid-cols-[90px_1fr_250px]`}>{team_2.rank === 1 && <div className="absolute -top-3 -right-3 rotate-3 rounded-sm bg-[#24122f] px-3 py-1 font-sans text-xs font-black text-[#ffd966] uppercase shadow-[3px_3px_0_#e2c078]">🏆 Champion (provisional)</div>}{isRelegation && <div className="absolute -top-3 -right-3 -rotate-2 rounded-sm bg-[#7a1d1d] px-3 py-1 font-sans text-xs font-black text-[#ffd0d0] uppercase shadow-[3px_3px_0_#c8a0a0]">Relegation</div>}<div><p className={`font-sans text-5xl font-black ${team_2.rank === 1 ? "text-[#a37d2c]" : isRelegation ? "text-[#7a1d1d]" : ""}`}>{team_2.rank}</p><p className="text-sm font-black text-[#8a3f31]">{team_2.movement}</p></div><div><h3 className="font-sans text-3xl font-semibold">{team_2.team}</h3><p className="mt-1 text-sm font-black text-[#6f6078] uppercase">{team_2.title}</p><p className="mt-4 max-w-3xl text-base leading-7 font-semibold text-[#514458]">{team_2.case}</p></div><div className="border-l-0 border-[#24122f] pt-2 md:border-l md:pl-5"><p className="text-xs font-black text-[#8a3f31] uppercase">The number</p><p className="mt-2 text-sm leading-6 font-black">{team_2.receipt}</p><p className="mt-4 text-xs font-black text-[#6f6078] uppercase">Open dossier</p></div>{contractTag && <div className={`-mx-5 mt-4 -mb-5 border-t px-5 py-3 text-xs font-bold md:col-span-3 ${tier === "champion" ? "border-[#e2c078] bg-[#fff5d6]/60 text-[#7a5a1d]" : tier === "top" ? "border-[#d7c9b7] bg-[#ede4d2]/60 text-[#5f4e2a]" : "border-[#c8a0a0] bg-[#f0c8c8]/60 text-[#7a1d1d]"}`}>{contractTag}</div>}</div>;
          })}</div></div></section>;
    t33 = <WhatIfShuffle />;
    t34 = <ManagementMap onTeamSelect={openDossier} />;
    $[43] = t32;
    $[44] = t33;
    $[45] = t34;
  } else {
    t32 = $[43];
    t33 = $[44];
    t34 = $[45];
  }
  let t35;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div><h2 className="font-sans text-5xl font-semibold">The draft board did not survive the season.</h2></div>;
    $[46] = t35;
  } else {
    t35 = $[46];
  }
  let t36;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = event_0 => setSelectedTeam(event_0.target.value);
    $[47] = t36;
  } else {
    t36 = $[47];
  }
  let t37;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = builds.map(_temp4);
    $[48] = t37;
  } else {
    t37 = $[48];
  }
  let t38;
  if ($[49] !== selectedTeam) {
    t38 = <select value={selectedTeam} onChange={t36} className="w-full appearance-none border border-[#24122f] bg-[#f7f0e6] px-4 py-3 pr-10 font-sans text-lg font-black outline-none">{t37}</select>;
    $[49] = selectedTeam;
    $[50] = t38;
  } else {
    t38 = $[50];
  }
  let t39;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ChevronsUpDown className="pointer-events-none absolute top-1/2 right-3 size-5 -translate-y-1/2" />;
    $[51] = t39;
  } else {
    t39 = $[51];
  }
  let t40;
  if ($[52] !== t38) {
    t40 = <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_360px]">{t35}<label className="flex flex-col gap-2 text-sm font-black uppercase">Choose a club<span className="relative">{t38}{t39}</span></label></div>;
    $[52] = t38;
    $[53] = t40;
  } else {
    t40 = $[53];
  }
  let t41;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <span>Points for</span>;
    $[54] = t41;
  } else {
    t41 = $[54];
  }
  let t42;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span>Roster chaos</span>;
    $[55] = t42;
  } else {
    t42 = $[55];
  }
  const t43 = fmtSigned(activeTeam.scheduleLuck);
  let t44;
  if ($[56] !== t43) {
    t44 = <div className="border border-[#d7c9b7] bg-white/50 p-3">Schedule luck<p className="font-sans text-3xl font-black">{t43}</p></div>;
    $[56] = t43;
    $[57] = t44;
  } else {
    t44 = $[57];
  }
  let t45;
  if ($[58] !== activeTeam) {
    t45 = teamMvps[activeTeam.team] && <div className="mt-6 border-t-4 border-[#e2c078] bg-[#24122f] p-5 text-white"><p className="text-xs font-black tracking-[0.3em] text-[#e2c078] uppercase">Load-bearing MVP</p><div className="mt-3 flex items-center gap-4">{teamMvps[activeTeam.team].code && <Image src={`/fantasy/headshots/p${teamMvps[activeTeam.team].code}.png`} alt={`${teamMvps[activeTeam.team].player} headshot`} width={64} height={80} className="h-20 w-16 flex-shrink-0 border-2 border-[#e2c078] bg-[#3a2540] object-cover" />}<div className="min-w-0"><h4 className="font-sans text-3xl leading-tight font-semibold">{teamMvps[activeTeam.team].player}</h4><p className="mt-1 text-xs font-bold tracking-wide text-[#a8a0b0] uppercase">{teamMvps[activeTeam.team].totalPoints} pts ·{" "}{teamMvps[activeTeam.team].weeksRostered} GWs</p></div></div><p className="mt-4 text-sm leading-6 font-semibold text-[#e8ddca]">Without him, the team loses{" "}<span className="font-black text-[#ffd966]">{teamMvps[activeTeam.team].loadBearingWins} wins</span>. Calculated by replaying every match without his points. Not raw scoring, actual margin of victory.</p></div>;
    $[58] = activeTeam;
    $[59] = t45;
  } else {
    t45 = $[59];
  }
  let t46;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <div className="flex items-center gap-3"><Shuffle className="size-5" /><h3 className="font-sans text-3xl font-semibold">Draft night versus now</h3></div>;
    $[60] = t46;
  } else {
    t46 = $[60];
  }
  let t47;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <p className="mb-3 text-xs font-black text-[#8a3f31] uppercase">Draft board identity</p>;
    $[61] = t47;
  } else {
    t47 = $[61];
  }
  let t48;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="mb-3 text-xs font-black text-[#4b8065] uppercase">Current roster identity</p>;
    $[62] = t48;
  } else {
    t48 = $[62];
  }
  const t49 = activeBuild.now.map(_temp6);
  let t50;
  if ($[63] !== t49) {
    t50 = <div>{t48}<div className="grid gap-2">{t49}</div></div>;
    $[63] = t49;
    $[64] = t50;
  } else {
    t50 = $[64];
  }
  let t51;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <p className="text-xs font-black text-[#d8cbb9] uppercase">Persona change</p>;
    $[65] = t51;
  } else {
    t51 = $[65];
  }
  let t52;
  if ($[66] !== activeBuild) {
    t52 = <div>{t51}<p className="mt-2 text-xl leading-7 font-black">{activeBuild.identity}</p></div>;
    $[66] = activeBuild;
    $[67] = t52;
  } else {
    t52 = $[67];
  }
  let t53;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <p className="text-xs font-black text-[#d8cbb9] uppercase">Biggest swing</p>;
    $[68] = t53;
  } else {
    t53 = $[68];
  }
  let t54;
  if ($[69] !== activeBuild) {
    t54 = <div>{t53}<p className="mt-2 text-xl leading-7 font-black">{activeBuild.swing}</p></div>;
    $[69] = activeBuild;
    $[70] = t54;
  } else {
    t54 = $[70];
  }
  let t55;
  if ($[71] !== t52 || $[72] !== t54) {
    t55 = <article className="grid gap-5 border border-[#d7c9b7] bg-[#24122f] p-6 text-white md:grid-cols-2">{t52}{t54}</article>;
    $[71] = t52;
    $[72] = t54;
    $[73] = t55;
  } else {
    t55 = $[73];
  }
  let t56;
  let t57;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <TradeHeadToHead />;
    t57 = <RegretFiles />;
    $[74] = t56;
    $[75] = t57;
  } else {
    t56 = $[74];
    t57 = $[75];
  }
  let t58;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="mb-8 grid gap-6 lg:grid-cols-[0.8fr_1.2fr]"><div><p className="text-sm font-black text-[#8a3f31] uppercase">One-player teams</p><h2 className="font-sans text-5xl font-semibold">Hero ball or hostage situation.</h2></div><p className="text-lg leading-8 font-semibold text-[#5f5364]">Team scores hide how lopsided a week actually was. Reece James cooked for 60% of Old Lady's GW8 score, and Nic still lost. Saka outscored most of Shyam's roster combined in GW26, and Shyam, allegedly, did not notice. These are dependency reports, not player highlights. If one name is half the team score, you're leasing the roster.</p></div>;
    $[76] = t58;
  } else {
    t58 = $[76];
  }
  let t59;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <section className="fantasy-reveal mx-auto max-w-7xl px-5 py-16 sm:px-8">{t58}<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{oneManShows.map(_temp7)}</div></section>;
    $[77] = t59;
  } else {
    t59 = $[77];
  }
  let t60;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <div className="mb-8 grid gap-6 lg:grid-cols-[0.8fr_1.2fr]"><div><p className="text-sm font-black text-[#e2c078] uppercase">More staff findings</p><h2 className="font-sans text-5xl font-semibold">Empty-cal stats. Mickey-Mouse rings. Asterisks attached.</h2></div><p className="text-lg leading-8 font-semibold text-[#fff4df]">Some findings deserve a full chart. Some only deserve one number and the appropriate amount of staff concern. These are the latter.</p></div>;
    $[78] = t60;
  } else {
    t60 = $[78];
  }
  let t61;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <section className="fantasy-reveal border-y border-[#24122f] bg-[#4f6f3b] text-white"><div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">{t60}<div className="grid gap-4 md:grid-cols-2">{seasonNuggets.map(_temp8)}</div></div></section>;
    $[79] = t61;
  } else {
    t61 = $[79];
  }
  let t62;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <div><p className="text-sm font-black text-[#8a3f31] uppercase">Chart room</p><h2 className="font-sans text-5xl font-semibold">The exhibits are now entered.</h2></div>;
    $[80] = t62;
  } else {
    t62 = $[80];
  }
  let t63;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = event_1 => setSelectedChart(event_1.target.value);
    $[81] = t63;
  } else {
    t63 = $[81];
  }
  let t64;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = charts.map(_temp9);
    $[82] = t64;
  } else {
    t64 = $[82];
  }
  let t65;
  if ($[83] !== selectedChart) {
    t65 = <select value={selectedChart} onChange={t63} className="w-full appearance-none border border-[#24122f] bg-[#f7f0e6] px-4 py-3 pr-10 font-sans text-lg font-black outline-none">{t64}</select>;
    $[83] = selectedChart;
    $[84] = t65;
  } else {
    t65 = $[84];
  }
  let t66;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <ChevronsUpDown className="pointer-events-none absolute top-1/2 right-3 size-5 -translate-y-1/2" />;
    $[85] = t66;
  } else {
    t66 = $[85];
  }
  let t67;
  if ($[86] !== t65) {
    t67 = <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_380px]">{t62}<label className="flex flex-col gap-2 text-sm font-black uppercase">Change graphic<span className="relative">{t65}{t66}</span></label></div>;
    $[86] = t65;
    $[87] = t67;
  } else {
    t67 = $[87];
  }
  const T0 = DownloadableChart;
  const t68 = `/fantasy/charts/${activeChart.slug}`;
  let t69;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = /\.[^.]+$/;
    $[88] = t69;
  } else {
    t69 = $[88];
  }
  const t70 = `ipl-${activeChart.slug.replace(t69, "")}.png`;
  let t71;
  if ($[89] !== T0 || $[90] !== activeChart.title || $[91] !== t68 || $[92] !== t70) {
    t71 = <T0 src={t68} alt={activeChart.title} filename={t70} />;
    $[89] = T0;
    $[90] = activeChart.title;
    $[91] = t68;
    $[92] = t70;
    $[93] = t71;
  } else {
    t71 = $[93];
  }
  let t72;
  if ($[94] !== activeChart.note) {
    t72 = <p className="mt-4 border border-[#4ea97e] bg-[#fffaf1] px-4 py-3 text-base leading-7 font-semibold text-[#514458]">{activeChart.note}</p>;
    $[94] = activeChart.note;
    $[95] = t72;
  } else {
    t72 = $[95];
  }
  let t73;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <div className="mb-6 flex items-center gap-3"><BarChart3 className="size-6" /><h3 className="font-sans text-4xl font-semibold">Full graphics appendix</h3></div>;
    $[96] = t73;
  } else {
    t73 = $[96];
  }
  let t74;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <div className="mt-12">{t73}<div className="grid gap-6">{charts.map(_temp10)}</div></div>;
    $[97] = t74;
  } else {
    t74 = $[97];
  }
  let t75;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <DataRoom />;
    $[98] = t75;
  } else {
    t75 = $[98];
  }
  let t76;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = <div><p className="flex items-center gap-2 text-sm font-black text-[#8a3f31] uppercase"><Flame className="size-4" />Funniest evidence</p><h2 className="mt-2 font-sans text-5xl font-semibold">The bad beats are where the season gets mean.</h2></div>;
    $[99] = t76;
  } else {
    t76 = $[99];
  }
  let t77;
  let t78;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8"><div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">{t76}<div className="grid gap-3">{badBeats.map(_temp11)}</div></div></section>;
    t78 = <RunInBoard />;
    $[100] = t77;
    $[101] = t78;
  } else {
    t77 = $[100];
    t78 = $[101];
  }
  let t79;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <section className="border-t border-[#24122f] bg-[#24122f] px-5 py-16 text-white sm:px-8"><div className="mx-auto max-w-4xl text-center"><p className="text-sm font-black text-[#e2c078] uppercase">IPL Staff Writers</p><h2 className="mt-3 font-sans text-5xl font-semibold">The staff will be accepting apologies in advance.</h2><p className="mt-5 text-lg leading-8 font-semibold text-[#e8ddca]">Three gameweeks left. Rohit has the lead. Teddy has the points and the grievance. Jonathan has a case for reconsideration that should be heard in chambers. Leyland has accepted no waivers and remains fourth, which the Staff continues to find personally insulting. Hugh's 89-pt Mickey Mouse banner will hang regardless. The 7 Down is still close enough to drag a middle-class team into the relegation conversation. Finish the Rosé. Hit the wire. Pray your opponent has a life.</p></div></section>;
    $[102] = t79;
  } else {
    t79 = $[102];
  }
  return <div className="min-h-screen bg-[#f7f0e6] text-[#24122f]">{t7}{t22}{t27}{t28}{t30}{t32}{t33}{t34}<section id="dossiers" className="fantasy-reveal border-y border-[#24122f] bg-[#fffaf1]"><div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">{t40}<div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr]"><aside className="border border-[#24122f] bg-[#f7f0e6] p-6 shadow-[8px_8px_0_#24122f]"><div className="flex items-center gap-4"><InitialBadge name={activeTeam.manager} /><div><p className="text-xs font-black text-[#8a3f31] uppercase">{activeTeam.manager}</p><h3 className="font-sans text-4xl font-semibold">{activeTeam.team}</h3></div></div><p className="mt-5 text-xl font-black">{activeTeam.persona}</p><p className="mt-3 text-base leading-7 font-semibold text-[#514458]">{activeTeam.verdict}</p><div className="mt-8 grid gap-5"><div><div className="mb-2 flex justify-between text-sm font-black">{t41}<span>{activeTeam.pointsFor}</span></div><MiniBar value={activeTeam.pointsFor} max={1515} /></div><div><div className="mb-2 flex justify-between text-sm font-black">{t42}<span>{activeTeam.chaos}</span></div><MiniBar value={activeTeam.chaos} max={85} tone="red" /></div><div className="grid grid-cols-2 gap-3 text-sm font-black"><div className="border border-[#d7c9b7] bg-white/50 p-3">Table rank<p className="font-sans text-3xl font-black">{activeTeam.rank}</p></div>{t44}</div></div>{t45}</aside><div className="grid gap-6"><article className="border border-[#d7c9b7] bg-[#f7f0e6] p-6">{t46}<div className="mt-6 grid gap-4 md:grid-cols-2"><div>{t47}<div className="grid gap-2">{activeBuild.draft.map(_temp5)}</div></div>{t50}</div></article>{t55}</div></div></div></section>{t56}{t57}{t59}{t61}<section id="charts" className="fantasy-reveal border-y border-[#24122f] bg-[#fffaf1]"><div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">{t67}<figure className="border border-[#d7c9b7] bg-[#f7f0e6] p-4 shadow-[8px_8px_0_#24122f]"><div className="mb-4 flex flex-col justify-between gap-2 md:flex-row md:items-end"><div><p className="text-xs font-black text-[#8a3f31] uppercase">{activeChart.eyebrow}</p><h3 className="font-sans text-4xl font-semibold">{activeChart.title}</h3></div><p className="max-w-xl text-sm leading-6 font-bold text-[#6d5d70]">{activeChart.copy}</p></div>{t71}{t72}</figure>{t74}</div></section>{t75}{t77}{t78}{t79}</div>;
}
function _temp11(t0) {
  const [gw, team_4, score, note] = t0;
  return <div key={`${gw}-${team_4}`} className="grid gap-2 border-b border-[#d7c9b7] py-4 md:grid-cols-[90px_1fr_1.2fr_1.2fr]"><p className="font-sans text-2xl font-black">{gw}</p><p className="font-black">{team_4}</p><p className="font-semibold text-[#514458]">{score}</p><p className="font-semibold text-[#8a3f31]">{note}</p></div>;
}
function _temp10(chart_1) {
  return <figure key={chart_1.slug} className="border border-[#d7c9b7] bg-[#f7f0e6] p-4"><figcaption className="mb-4 flex flex-col justify-between gap-2 md:flex-row md:items-end"><div><p className="text-xs font-black text-[#8a3f31] uppercase">{chart_1.eyebrow}</p><h4 className="font-sans text-3xl font-semibold">{chart_1.title}</h4></div><p className="max-w-xl text-sm leading-6 font-bold text-[#6d5d70]">{chart_1.copy}</p></figcaption><DownloadableChart src={`/fantasy/charts/${chart_1.slug}`} alt={chart_1.title} filename={`ipl-${chart_1.slug.replace(/\.[^.]+$/, "")}.png`} /><p className="mt-4 border border-[#4ea97e] bg-[#fffaf1] px-4 py-3 text-sm leading-6 font-semibold text-[#514458]">{chart_1.note}</p></figure>;
}
function _temp9(chart_0) {
  return <option key={chart_0.slug} value={chart_0.slug}>{chart_0.title}</option>;
}
function _temp8(nugget) {
  return <article key={nugget.title} className="grid gap-5 border border-white/40 bg-white/10 p-5 backdrop-blur lg:grid-cols-[150px_1fr]"><div className="flex min-h-28 items-center border-b border-white/20 pb-4 lg:border-r lg:border-b-0 lg:pr-5 lg:pb-0"><NuggetStat stat={nugget.stat} /></div><div><h3 className="font-sans text-2xl font-semibold">{nugget.title}</h3><p className="mt-3 text-sm leading-6 font-semibold text-[#fff4df]">{nugget.body}</p></div></article>;
}
function _temp7(show) {
  return <article key={`${show.player}-${show.week}`} className="overflow-hidden border border-[#d7c9b7] bg-[#fffaf1]"><div className="flex items-end justify-between gap-4 border-b border-[#d7c9b7] bg-[#f7f0e6] p-5"><div><p className="text-xs font-black text-[#8a3f31] uppercase">{show.week} · {show.team}</p><h3 className="font-sans text-3xl font-semibold">{show.player}</h3></div><PlayerPortrait player={show.player} size="lg" /></div><div className="p-5"><div className="mb-4 flex items-end justify-between"><div><p className="font-sans text-5xl font-black">{show.share}%</p><p className="text-xs font-black text-[#6f6078] uppercase">of team score</p></div><p className="text-right text-sm font-black">{show.points}/{show.teamPoints}<br /><span className="text-[#8a3f31]">{show.result}</span></p></div><div className="h-3 rounded-full bg-[#e8ddca]"><div className="h-full rounded-full bg-[#bf3b53]" style={{
          width: `${show.share}%`
        }} /></div><p className="mt-4 text-sm leading-6 font-semibold text-[#514458]">{show.note}</p></div></article>;
}
function _temp6(player_0) {
  return <PlayerTile key={player_0} label={player_0} tone="current" />;
}
function _temp5(player) {
  return <PlayerTile key={player} label={player} tone="draft" />;
}
function _temp4(team_3) {
  return <option key={team_3.team}>{team_3.team}</option>;
}
function _temp3(award) {
  return <article key={award.title} className="border border-[#6c5876] bg-[#fffaf1] p-5 text-[#24122f]"><div className="flex items-start justify-between gap-4"><div><p className="text-xs font-black text-[#8a3f31] uppercase">{award.title}</p><h3 className="mt-2 font-sans text-3xl font-semibold">{award.winner}</h3></div>{"player" in award && award.player ? <PlayerPortrait player={award.player} size="sm" /> : null}</div><p className="mt-4 text-sm leading-6 font-semibold text-[#514458]">{award.detail}</p><p className="mt-5 border-t border-[#d7c9b7] pt-4 font-sans text-2xl font-black">{award.stat}</p></article>;
}
function _temp2() {}
function _temp() {}
export default function FantasyPage() {
  return useFantasyPageView();
}